<template>
  <div class="wc" :class="{ 'wc--visible': visible, 'wc--flash': flash }">
    <!-- ── Background effects ── -->
    <div class="wc-accent"></div>
    <div class="wc-bg"></div>
    <div
      class="wc-glow"
      :class="isActive ? 'wc-glow-green' : 'wc-glow-amber'"
    ></div>
    <div class="wc-scan"></div>

    <!-- ── Ambient particles ── -->
    <div class="wc-particles">
      <div v-for="i in 10" :key="i" class="wc-pt" :class="`wc-pt-${i}`"></div>
    </div>

    <!-- ── Loading ── -->
    <div v-if="loading" class="wc-loading">
      <div class="wc-spinner"></div>
      <p class="wc-loading-label">Checking your setup…</p>
    </div>

    <template v-else>
      <!-- ═══ Stage 1: Icon ═══ -->
      <div class="wc-icon" :class="{ show: stage >= 1 }">
        <div
          class="wc-core"
          :class="isActive ? 'wc-core-green' : 'wc-core-amber'"
        >
          <template v-if="isActive">
            <svg viewBox="0 0 24 24" fill="none" class="wc-check">
              <path
                d="M7 12l4 4 6-7"
                stroke="white"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </template>
          <span v-else class="wc-rocket">🚀</span>
        </div>

        <!-- Expanding glow rings -->
        <div
          class="wc-ring wc-r1"
          :class="isActive ? 'wc-ring-green' : 'wc-ring-amber'"
        ></div>
        <div
          class="wc-ring wc-r2"
          :class="isActive ? 'wc-ring-green' : 'wc-ring-amber'"
        ></div>
        <div
          class="wc-ring wc-r3"
          :class="isActive ? 'wc-ring-green' : 'wc-ring-amber'"
        ></div>

        <!-- Orbiting dots -->
        <div
          class="wc-orbit wc-orb-1"
          :class="isActive ? 'wc-orbit-green' : 'wc-orbit-amber'"
        ></div>
        <div
          class="wc-orbit wc-orb-2"
          :class="isActive ? 'wc-orbit-green' : 'wc-orbit-amber'"
        ></div>

        <!-- Burst particles (on appear) -->
        <div v-if="burst" class="wc-burst">
          <div
            v-for="j in 8"
            :key="j"
            class="wc-bd"
            :class="`wc-bd-${j}`"
          ></div>
        </div>
      </div>

      <!-- ═══ Stage 2: Hero text ═══ -->
      <div class="wc-hero" :class="{ show: stage >= 2 }">
        <div v-if="isActive" class="wc-live">
          <span class="wc-live-dot"></span>
          LIVE
        </div>
        <h2 class="wc-title">
          {{ isActive ? 'Your AI is Live' : 'Ready to Launch' }}
        </h2>
        <p class="wc-sub">
          {{
            isActive
              ? 'Analyzing customers and sending personalized messages — completely on autopilot.'
              : "Everything's configured. One click and your AI starts reaching customers immediately."
          }}
        </p>
      </div>

      <!-- ═══ Stage 3: Feature grid ═══ -->
      <div class="wc-grid" :class="{ show: stage >= 3 }">
        <div
          v-for="(f, i) in features"
          :key="i"
          class="wc-cell"
          :style="{ transitionDelay: `${i * 80}ms` }"
        >
          <div class="wc-cell-icon" :class="`wc-ci-${f.color}`">
            {{ f.icon }}
          </div>
          <span class="wc-cell-name">{{ f.label }}</span>
          <span class="wc-cell-chk">✓</span>
        </div>
      </div>

      <!-- ═══ Stage 4: Actions ═══ -->
      <div class="wc-actions" :class="{ show: stage >= 4 }">
        <template v-if="isActive">
          <button class="wc-btn wc-btn-dash" @click="$emit('close')">
            Go to Dashboard
            <span class="wc-arrow">→</span>
          </button>
        </template>
        <template v-else>
          <transition name="wc-swap" mode="out-in">
            <button
              v-if="activated"
              key="done"
              class="wc-btn wc-btn-done"
              @click="$emit('close')"
            >
              ✓ You're Live — Go to Dashboard
              <span class="wc-arrow">→</span>
            </button>
            <button
              v-else
              key="go"
              class="wc-btn wc-btn-go"
              :disabled="activating"
              @click="$emit('activate')"
            >
              <span v-if="activating" class="wc-btn-spin"></span>
              {{ activating ? 'Activating…' : '⚡ Go Live' }}
            </button>
          </transition>
        </template>

        <div class="wc-secondary">
          <button v-if="!isActive" class="wc-link" @click="$emit('close')">
            Explore first
          </button>
          <span v-if="!isActive" class="wc-sep">·</span>
          <button class="wc-link" @click="$emit('replay')">Replay Tour</button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable no-undef */
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';

const props = defineProps<{
  loading: boolean;
  isActive: boolean | null;
  activating: boolean;
  activated: boolean;
}>();

defineEmits<{
  close: [];
  activate: [];
  replay: [];
}>();

const features = [
  { icon: '📊', label: 'Dashboard', color: 'blue' },
  { icon: '👥', label: 'Customers', color: 'purple' },
  { icon: '🧠', label: 'AI Engine', color: 'amber' },
  { icon: '✉️', label: 'Templates', color: 'green' },
  { icon: '📋', label: 'Lead Forms', color: 'rose' },
  { icon: '🔌', label: 'Integrations', color: 'teal' }
];

const visible = ref(false);
const stage = ref(0);
const burst = ref(false);
const flash = ref(false);
let timers: ReturnType<typeof setTimeout>[] = [];

function start() {
  stop();
  visible.value = true;
  const t = (ms: number, fn: () => void) => timers.push(setTimeout(fn, ms));

  t(100, () => {
    stage.value = 1;
    burst.value = true;
  });
  t(700, () => {
    stage.value = 2;
  });
  t(1300, () => {
    stage.value = 3;
  });
  t(1900, () => {
    stage.value = 4;
  });
  t(1400, () => {
    burst.value = false;
  });
}

function stop() {
  timers.forEach(clearTimeout);
  timers = [];
  stage.value = 0;
  visible.value = false;
  burst.value = false;
}

// Start animation once loading finishes
watch(
  () => props.loading,
  (now, prev) => {
    if (prev === true && now === false) {
      nextTick(() => start());
    }
  }
);

// Flash when activation succeeds
watch(
  () => props.activated,
  (now, prev) => {
    if (now && !prev) {
      flash.value = true;
      timers.push(setTimeout(() => (flash.value = false), 1000));
    }
  }
);

onMounted(() => {
  if (!props.loading) start();
});

onUnmounted(() => stop());
</script>

<style scoped>
/* ════════════════════════════════════════
   ROOT — light theme
   ════════════════════════════════════════ */
.wc {
  position: relative;
  background: #ffffff;
  padding: 44px 36px 36px;
  text-align: center;
  overflow: hidden;
  opacity: 0;
  transform: scale(0.97);
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.wc--visible {
  opacity: 1;
  transform: scale(1);
}

.wc--flash {
  animation: wc-flash 0.8s ease;
}

@keyframes wc-flash {
  0% {
    box-shadow: inset 0 0 0 rgba(34, 197, 94, 0);
  }
  30% {
    box-shadow: inset 0 0 60px rgba(34, 197, 94, 0.08);
  }
  100% {
    box-shadow: inset 0 0 0 rgba(34, 197, 94, 0);
  }
}

/* ════════════════════════════════════════
   BACKGROUND EFFECTS
   ════════════════════════════════════════ */

.wc-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #6366f1, #8b5cf6, #22c55e, #6366f1);
  background-size: 200% 100%;
  animation: wc-gradient-shift 3s linear infinite;
  z-index: 2;
}

@keyframes wc-gradient-shift {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
}

.wc-bg {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 0, 0, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
  pointer-events: none;
}

.wc-glow {
  position: absolute;
  top: -20%;
  left: 50%;
  width: 120%;
  height: 60%;
  transform: translateX(-50%);
  border-radius: 50%;
  pointer-events: none;
  transition: background 0.6s;
}

.wc-glow-green {
  background: radial-gradient(
    circle,
    rgba(34, 197, 94, 0.07) 0%,
    transparent 65%
  );
}

.wc-glow-amber {
  background: radial-gradient(
    circle,
    rgba(245, 158, 11, 0.07) 0%,
    transparent 65%
  );
}

.wc-scan {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(99, 102, 241, 0.06),
    transparent
  );
  pointer-events: none;
  animation: wc-scanmove 5s linear infinite;
}

@keyframes wc-scanmove {
  0% {
    top: -2%;
  }
  100% {
    top: 102%;
  }
}

/* ════════════════════════════════════════
   FLOATING PARTICLES
   ════════════════════════════════════════ */
.wc-particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.wc-pt {
  position: absolute;
  border-radius: 50%;
  opacity: 0;
  animation: wc-float 7s ease-in-out infinite;
}

.wc-pt-1 {
  width: 4px;
  height: 4px;
  background: #a5b4fc;
  top: 12%;
  left: 8%;
  animation-delay: 0s;
}
.wc-pt-2 {
  width: 3px;
  height: 3px;
  background: #6ee7b7;
  top: 20%;
  right: 12%;
  animation-delay: 1s;
}
.wc-pt-3 {
  width: 4px;
  height: 4px;
  background: #fcd34d;
  bottom: 22%;
  left: 6%;
  animation-delay: 2s;
}
.wc-pt-4 {
  width: 3px;
  height: 3px;
  background: #f9a8d4;
  bottom: 16%;
  right: 9%;
  animation-delay: 3s;
}
.wc-pt-5 {
  width: 3px;
  height: 3px;
  background: #93c5fd;
  top: 38%;
  left: 4%;
  animation-delay: 0.5s;
}
.wc-pt-6 {
  width: 4px;
  height: 4px;
  background: #c4b5fd;
  top: 48%;
  right: 5%;
  animation-delay: 1.5s;
}
.wc-pt-7 {
  width: 3px;
  height: 3px;
  background: #6ee7b7;
  bottom: 38%;
  left: 10%;
  animation-delay: 2.5s;
}
.wc-pt-8 {
  width: 3px;
  height: 3px;
  background: #fcd34d;
  bottom: 42%;
  right: 12%;
  animation-delay: 3.5s;
}
.wc-pt-9 {
  width: 3px;
  height: 3px;
  background: #a5b4fc;
  top: 62%;
  left: 16%;
  animation-delay: 4s;
}
.wc-pt-10 {
  width: 4px;
  height: 4px;
  background: #f9a8d4;
  top: 68%;
  right: 18%;
  animation-delay: 4.5s;
}

@keyframes wc-float {
  0%,
  100% {
    opacity: 0;
    transform: translateY(0) scale(0.5);
  }
  20% {
    opacity: 0.35;
  }
  50% {
    opacity: 0.25;
    transform: translateY(-22px) scale(1);
  }
  80% {
    opacity: 0.3;
  }
}

/* ════════════════════════════════════════
   LOADING
   ════════════════════════════════════════ */
.wc-loading {
  padding: 60px 0;
  position: relative;
  z-index: 1;
}

.wc-spinner {
  width: 32px;
  height: 32px;
  border: 2px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  margin: 0 auto 14px;
  animation: wc-spin 0.8s linear infinite;
}

@keyframes wc-spin {
  to {
    transform: rotate(360deg);
  }
}

.wc-loading-label {
  font-size: 14px;
  font-weight: 600;
  color: #9ca3af;
  margin: 0;
}

/* ════════════════════════════════════════
   SHARED STAGE TRANSITIONS
   ════════════════════════════════════════ */
.wc-icon,
.wc-hero,
.wc-grid,
.wc-actions {
  opacity: 0;
  transform: translateY(18px);
  transition: opacity 0.55s ease, transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);
  position: relative;
  z-index: 1;
}

.wc-icon.show,
.wc-hero.show,
.wc-grid.show,
.wc-actions.show {
  opacity: 1;
  transform: translateY(0);
}

/* ════════════════════════════════════════
   STAGE 1: ICON AREA
   ════════════════════════════════════════ */
.wc-icon {
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.wc-core {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
}

.wc-core-green {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  box-shadow: 0 8px 30px rgba(34, 197, 94, 0.3);
}

.wc-core-amber {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  box-shadow: 0 8px 30px rgba(245, 158, 11, 0.3);
}

.wc-check {
  width: 32px;
  height: 32px;
  stroke-dasharray: 30;
  stroke-dashoffset: 30;
}

.wc-icon.show .wc-check {
  animation: wc-draw 0.5s 0.3s ease forwards;
}

@keyframes wc-draw {
  to {
    stroke-dashoffset: 0;
  }
}

.wc-rocket {
  font-size: 32px;
  display: inline-block;
  animation: wc-bob 2.5s ease-in-out infinite;
}

@keyframes wc-bob {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

/* ── Expanding glow rings ── */
.wc-ring {
  position: absolute;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  pointer-events: none;
  opacity: 0;
}

.wc-ring-green {
  border-color: rgba(34, 197, 94, 0.15);
}
.wc-ring-amber {
  border-color: rgba(245, 158, 11, 0.15);
}

.wc-r1 {
  width: 105px;
  height: 105px;
  border: 1.5px solid;
}
.wc-r2 {
  width: 136px;
  height: 136px;
  border: 1px solid;
}
.wc-r3 {
  width: 168px;
  height: 168px;
  border: 1px solid;
}

.wc-icon.show .wc-r1 {
  animation: wc-ring-expand 1s 0.15s ease-out forwards;
}
.wc-icon.show .wc-r2 {
  animation: wc-ring-expand 1.3s 0.3s ease-out forwards;
}
.wc-icon.show .wc-r3 {
  animation: wc-ring-expand 1.6s 0.45s ease-out forwards;
}

@keyframes wc-ring-expand {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.5);
  }
  25% {
    opacity: 0.7;
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(1);
  }
}

/* ── Orbiting dots ── */
.wc-orbit {
  position: absolute;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.8s ease 0.5s;
}

.wc-icon.show .wc-orbit {
  opacity: 1;
}

.wc-orbit-green {
  border: 1px solid rgba(34, 197, 94, 0.08);
}
.wc-orbit-amber {
  border: 1px solid rgba(245, 158, 11, 0.08);
}

.wc-orb-1 {
  width: 112px;
  height: 112px;
  transform: translate(-50%, -50%);
}

.wc-orb-2 {
  width: 150px;
  height: 150px;
  transform: translate(-50%, -50%);
}

.wc-orbit::after {
  content: '';
  position: absolute;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  top: -2.5px;
  left: calc(50% - 2.5px);
  will-change: transform;
}

.wc-orbit-green::after {
  background: #22c55e;
  box-shadow: 0 0 6px rgba(34, 197, 94, 0.5);
}
.wc-orbit-amber::after {
  background: #f59e0b;
  box-shadow: 0 0 6px rgba(245, 158, 11, 0.5);
}

.wc-orb-1::after {
  transform-origin: 2.5px 58.5px;
  animation: wc-orbit-spin 5s linear infinite;
}

.wc-orb-2::after {
  transform-origin: 2.5px 77.5px;
  animation: wc-orbit-spin 8s linear infinite reverse;
}

@keyframes wc-orbit-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Burst particles ── */
.wc-burst {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  z-index: 3;
}

.wc-bd {
  position: absolute;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  animation: wc-burst-fly 0.7s ease-out forwards;
}

.wc-bd-1 {
  background: #22c55e;
  --bx: 38px;
  --by: -38px;
  animation-delay: 0.05s;
}
.wc-bd-2 {
  background: #818cf8;
  --bx: 50px;
  --by: 0px;
  animation-delay: 0.1s;
}
.wc-bd-3 {
  background: #34d399;
  --bx: 38px;
  --by: 38px;
  animation-delay: 0.15s;
}
.wc-bd-4 {
  background: #fbbf24;
  --bx: 0px;
  --by: 50px;
  animation-delay: 0.02s;
}
.wc-bd-5 {
  background: #f472b6;
  --bx: -38px;
  --by: 38px;
  animation-delay: 0.12s;
}
.wc-bd-6 {
  background: #60a5fa;
  --bx: -50px;
  --by: 0px;
  animation-delay: 0.08s;
}
.wc-bd-7 {
  background: #a78bfa;
  --bx: -38px;
  --by: -38px;
  animation-delay: 0.04s;
}
.wc-bd-8 {
  background: #22c55e;
  --bx: 0px;
  --by: -50px;
  animation-delay: 0.18s;
}

@keyframes wc-burst-fly {
  0% {
    opacity: 1;
    transform: translate(0, 0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translate(var(--bx), var(--by)) scale(0);
  }
}

/* ════════════════════════════════════════
   STAGE 2: HERO
   ════════════════════════════════════════ */
.wc-hero {
  margin-bottom: 22px;
  padding: 0 20px;
}

.wc-live {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.14em;
  color: #16a34a;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 999px;
  padding: 5px 14px;
  margin-bottom: 14px;
}

.wc-live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 6px rgba(34, 197, 94, 0.6);
  animation: wc-breathe 2s ease-in-out infinite;
}

@keyframes wc-breathe {
  0%,
  100% {
    opacity: 1;
    box-shadow: 0 0 6px rgba(34, 197, 94, 0.6);
  }
  50% {
    opacity: 0.4;
    box-shadow: 0 0 12px rgba(34, 197, 94, 0.6);
  }
}

.wc-title {
  margin: 0 0 10px;
  font-size: 28px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.03em;
  line-height: 1.15;
}

.wc-sub {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.65;
  max-width: 38ch;
  margin-left: auto;
  margin-right: auto;
}

/* ════════════════════════════════════════
   STAGE 3: FEATURE GRID
   ════════════════════════════════════════ */
.wc-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin: 0 0 28px;
  padding: 0 4px;
}

.wc-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 14px 8px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  position: relative;
  opacity: 0;
  transform: scale(0.92) translateY(8px);
  transition: opacity 0.4s ease, transform 0.4s ease, border-color 0.4s ease;
}

.wc-grid.show .wc-cell {
  opacity: 1;
  transform: scale(1) translateY(0);
  border-color: #e5e7eb;
}

.wc-cell-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.wc-ci-blue {
  background: #eff6ff;
}
.wc-ci-purple {
  background: #f5f3ff;
}
.wc-ci-amber {
  background: #fffbeb;
}
.wc-ci-green {
  background: #f0fdf4;
}
.wc-ci-rose {
  background: #fff1f2;
}
.wc-ci-teal {
  background: #f0fdfa;
}

.wc-cell-name {
  font-size: 11px;
  font-weight: 600;
  color: #4b5563;
  line-height: 1;
}

.wc-cell-chk {
  position: absolute;
  top: 6px;
  right: 8px;
  font-size: 10px;
  font-weight: 800;
  color: #22c55e;
}

/* ════════════════════════════════════════
   STAGE 4: ACTIONS
   ════════════════════════════════════════ */
.wc-actions {
  padding: 0 8px;
}

.wc-btn {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
  color: white;
}

.wc-btn-dash {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
  padding: 15px 24px;
  font-size: 15px;
}

.wc-btn-dash:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 24px rgba(99, 102, 241, 0.4);
}

.wc-btn-dash:active {
  transform: scale(0.98);
}

.wc-btn-go {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  box-shadow: 0 4px 16px rgba(34, 197, 94, 0.3);
  padding: 17px 28px;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.01em;
}

.wc-btn-go:not(:disabled) {
  animation: wc-glow-pulse 2.5s ease-in-out infinite;
}

.wc-btn-go:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 28px rgba(34, 197, 94, 0.4);
}

.wc-btn-go:active:not(:disabled) {
  transform: scale(0.98);
}

.wc-btn-go:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@keyframes wc-glow-pulse {
  0%,
  100% {
    box-shadow: 0 4px 16px rgba(34, 197, 94, 0.3);
  }
  50% {
    box-shadow: 0 4px 28px rgba(34, 197, 94, 0.45);
  }
}

.wc-btn-done {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  box-shadow: 0 4px 16px rgba(34, 197, 94, 0.3);
  padding: 15px 24px;
  font-size: 15px;
  animation: wc-pop 0.4s ease;
}

.wc-btn-done:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 24px rgba(34, 197, 94, 0.4);
}

@keyframes wc-pop {
  0% {
    transform: scale(0.9);
    opacity: 0;
  }
  60% {
    transform: scale(1.03);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.wc-btn-spin {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: wc-spin 0.7s linear infinite;
}

.wc-arrow {
  font-size: 16px;
  transition: transform 0.15s;
}

.wc-btn:hover .wc-arrow {
  transform: translateX(2px);
}

/* Secondary links */
.wc-secondary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 10px;
}

.wc-link {
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 8px;
  transition: color 0.15s;
}

.wc-link:hover {
  color: #6b7280;
}

.wc-sep {
  color: #d1d5db;
  font-size: 12px;
  user-select: none;
}

/* ════════════════════════════════════════
   BUTTON SWAP TRANSITION
   ════════════════════════════════════════ */
.wc-swap-enter-active,
.wc-swap-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.wc-swap-enter-from {
  opacity: 0;
  transform: scale(0.95) translateY(4px);
}

.wc-swap-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-4px);
}

/* ════════════════════════════════════════
   MOBILE
   ════════════════════════════════════════ */
@media (max-width: 520px) {
  .wc {
    padding: 36px 20px 30px;
  }

  .wc-icon {
    height: 130px;
  }

  .wc-core {
    width: 64px;
    height: 64px;
  }

  .wc-check {
    width: 28px;
    height: 28px;
  }

  .wc-rocket {
    font-size: 28px;
  }

  .wc-r1 {
    width: 90px;
    height: 90px;
  }
  .wc-r2 {
    width: 116px;
    height: 116px;
  }
  .wc-r3 {
    width: 142px;
    height: 142px;
  }

  .wc-orb-1 {
    width: 96px;
    height: 96px;
  }
  .wc-orb-2 {
    width: 126px;
    height: 126px;
  }

  .wc-orb-1::after {
    transform-origin: 2.5px 50.5px;
  }
  .wc-orb-2::after {
    transform-origin: 2.5px 65.5px;
  }

  .wc-title {
    font-size: 24px;
  }

  .wc-sub {
    font-size: 13px;
  }

  .wc-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .wc-cell-icon {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }

  .wc-btn-dash,
  .wc-btn-done {
    font-size: 14px;
    padding: 14px 20px;
  }

  .wc-btn-go {
    font-size: 15px;
    padding: 15px 22px;
  }
}
</style>
